// recurringReminders.js — Phase 208 / card 208-B1.
//
// Pure helper that turns a list of recurring rules into the reminders the
// dashboard should nudge about:
//
//   computeRecurringReminders({ rules, today, horizonDays = 7 })
//     → [{ rule, dueDate, daysUntil }]
//
// WHY THE DATE HELPERS MATTER
// ---------------------------
// `day_of_month` is a CALENDAR day, so the comparison against "today" must
// be made on the local calendar. `toISOString()` would shift the day in any
// negative-UTC-offset timezone — the Phase 207 trap. This module therefore
// uses the shared helpers from `frontend/src/lib/localDate.js` (card
// 208-E2) and the month helpers from `frontend/src/lib/forecast.js`
// (Phase 197) instead of re-implementing any date arithmetic.
//
// The function is PURE: no I/O, no Dexie, no implicit `Date.now()`. The
// caller injects `today`.

import { localDayDiff, localDate } from './localDate.js';
import { effectiveDay, ym } from './forecast.js';

/**
 * @param {Object} input
 * @param {Array<Object>} input.rules - recurring rules
 * @param {Date|string} input.today - today (a Date, or a 'YYYY-MM-DD' string)
 * @param {number} [input.horizonDays=7] - how many days ahead to look
 * @returns {Array<{rule: Object, dueDate: Date, daysUntil: number}>}
 */
export function computeRecurringReminders({ rules = [], today, horizonDays = 7 } = {}) {
  if (!(today instanceof Date) && typeof today !== 'string') {
    throw new Error('recurringReminders: today must be a Date or a YYYY-MM-DD string');
  }
  if (!Number.isFinite(horizonDays) || horizonDays < 0) {
    throw new Error('recurringReminders: horizonDays must be a non-negative number');
  }

  const todayMidnight = toLocalMidnight(today);
  const months = monthsSpanned(todayMidnight, horizonDays);

  const reminders = [];
  for (const rule of rules) {
    if (!isEligible(rule)) continue;

    const lead = leadDays(rule);
    for (const monthYm of months) {
      if (!withinEffectiveRange(rule, monthYm)) continue;

      const day = effectiveDay(rule, monthStart(monthYm));
      if (day === null) continue; // day_of_month exceeds this month's length

      const dueDate = localDate(`${monthYm}-${String(day).padStart(2, '0')}`);
      const daysUntil = localDayDiff(todayMidnight, dueDate);

      // 0 <= daysUntil <= reminder_days_before, and within the horizon.
      if (daysUntil < 0) continue;
      if (daysUntil > lead) continue;
      if (daysUntil > horizonDays) continue;

      reminders.push({ rule, dueDate, daysUntil });
    }
  }

  reminders.sort((a, b) => a.daysUntil - b.daysUntil);
  return reminders;
}

// ── helpers ────────────────────────────────────────────────────────────────

/** Local midnight for a Date or a 'YYYY-MM-DD' string. */
function toLocalMidnight(value) {
  const d = value instanceof Date ? value : localDate(value);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** The distinct 'YYYY-MM' months touched by [today, today + horizonDays]. */
function monthsSpanned(todayMidnight, horizonDays) {
  const seen = new Set();
  const out = [];
  for (let i = 0; i <= horizonDays; i++) {
    const d = new Date(todayMidnight.getFullYear(), todayMidnight.getMonth(), todayMidnight.getDate() + i);
    const monthYm = ym(d);
    if (seen.has(monthYm)) continue;
    seen.add(monthYm);
    out.push(monthYm);
  }
  return out;
}

/** A Date on the 1st of the given 'YYYY-MM' month. */
function monthStart(monthYm) {
  const [y, m] = monthYm.split('-').map(Number);
  return new Date(y, m - 1, 1);
}

/** A rule participates only when reminders are on and the rule is live. */
function isEligible(rule) {
  if (!rule) return false;
  if (!rule.reminder_enabled) return false;
  if (rule.active === 0 || rule.active === false) return false;
  if (rule.deleted) return false;
  return true;
}

/**
 * How many days ahead this rule may be nudged. The schema constrains
 * `reminder_days_before` to 0..7; a missing value means "today only".
 */
function leadDays(rule) {
  const n = Number(rule.reminder_days_before);
  return Number.isFinite(n) ? n : 0;
}

/** effective_from / effective_to are inclusive 'YYYY-MM' bounds. */
function withinEffectiveRange(rule, monthYm) {
  const from = rule.effective_from;
  const to = rule.effective_to;
  if (from && monthYm < from) return false;
  if (to && monthYm > to) return false;
  return true;
}
